
import { useState, useEffect } from 'react';
import { useRSSNews } from '../../hooks/useRSSNews';

export default function Home() {
  const [showModal, setShowModal] = useState<string | null>(null);
  const [showNews, setShowNews] = useState(true);
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // RSSニュースフックを使用
  const { news: newsHeadlines, loading: newsLoading, error: newsError, refreshNews } = useRSSNews();

  // ニュースの自動切り替え
  useEffect(() => {
    if (!showNews || newsHeadlines.length === 0) return;

    const interval = setInterval(() => {
      setCurrentNewsIndex((prev) => (prev + 1) % newsHeadlines.length);
    }, 5000); // 5秒ごとに切り替え

    return () => clearInterval(interval);
  }, [showNews, newsHeadlines.length]);

  const openModal = (content: string) => {
    setShowModal(content);
  };

  const closeModal = () => {
    setShowModal(null);
  };

  // 共通エラーハンドラ
  const handleFetchError = (error: unknown) => {
    console.error('Fetch error:', error);
    alert('送信に失敗しました。もう一度お試しください。');
  };

  const handleFreeJoin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d39g7nafkoblkdk5cqm0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(formData as any).toString(),
        }
      );

      if (response.ok) {
        alert('お申し込みありがとうございます！');
        (e.target as HTMLFormElement).reset();
        closeModal();
      } else {
        handleFetchError(new Error(`Status ${response.status}`));
      }
    } catch (error) {
      handleFetchError(error);
    }
  };

  const handleSupport = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d39g7nafkoblkdk5cqm0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(formData as any).toString(),
        }
      );

      if (response.ok) {
        alert('ご支援のお申し出をありがとうございます！');
        (e.target as HTMLFormElement).reset();
        closeModal();
      } else {
        handleFetchError(new Error(`Status ${response.status}`));
      }
    } catch (error) {
      handleFetchError(error);
    }
  };

  // 講座申し込みハンドラを修正
  const handleCourseSignup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d39g7nafkoblkdk5cqng',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(formData as any).toString(),
        }
      );

      if (response.ok) {
        alert('お申し込みありがとうございます！講座の詳細をメールでお送りいたします。');
        (e.target as HTMLFormElement).reset();
        closeModal();
      } else {
        handleFetchError(new Error(`Status ${response.status}`));
      }
    } catch (error) {
      handleFetchError(error);
    }
  };

  // お問い合わせハンドラを追加
  const handleContactInquiry = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d39g7nafkoblkdk5cqng',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(formData as any).toString(),
        }
      );

      if (response.ok) {
        alert('お問い合わせありがとうございます！返信をお待ちください。');
        (e.target as HTMLFormElement).reset();
        closeModal();
      } else {
        handleFetchError(new Error(`Status ${response.status}`));
      }
    } catch (error) {
      handleFetchError(error);
    }
  };

  // 安全なナビゲーションユーティリティ
  const safeNavigate = (path: string) => {
    if (typeof window?.REACT_APP_NAVIGATE === 'function') {
      window.REACT_APP_NAVIGATE(path);
    } else {
      console.error('REACT_APP_NAVIGATE is not defined or not a function');
    }
  };

  // 管理者アクセスハンドラを追加
  const handleAdminAccess = () => {
    setShowPasswordModal(true);
    setPassword('');
    setPasswordError('');
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === '7878') {
      setShowPasswordModal(false);
      if (typeof window.REACT_APP_NAVIGATE === 'function') {
        window.REACT_APP_NAVIGATE('/admin');
      } else {
        console.error('REACT_APP_NAVIGATE is not defined or not a function');
      }
    } else {
      setPasswordError('パスワードが正しくありません');
      setPassword('');
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPassword('');
    setPasswordError('');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ニュースバー */}
      {showNews && newsHeadlines.length > 0 && (
        <div
          className={`${
            newsHeadlines[currentNewsIndex]?.urgent
              ? 'bg-gradient-to-r from-red-600 to-orange-600'
              : 'bg-gradient-to-r from-blue-600 to-purple-600'
          } text-white px-6 py-3 relative transition-all duration-500`}
        >
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div
                    className={`w-2 h-2 ${
                      newsHeadlines[currentNewsIndex]?.urgent ? 'bg-yellow-400' : 'bg-white'
                    } rounded-full animate-pulse`}
                  ></div>
                  <span className="text-xs font-bold bg-white/20 px-2 py-1 rounded-full">
                    {newsHeadlines[currentNewsIndex]?.urgent ? '緊急' : 'NEWS'}
                  </span>
                  {newsLoading && (
                    <div className="flex items-center gap-1">
                      <div className="w-1 h-1 bg-white/60 rounded-full animate-pulse"></div>
                      <span className="text-xs text-white/80">更新中</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {newsHeadlines[currentNewsIndex]?.title}
                  </div>
                  <div className="text-xs opacity-80">
                    {newsHeadlines[currentNewsIndex]?.date}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                {/* 手動更新ボタン */}
                <button
                  onClick={refreshNews}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
                  title="ニュースを更新"
                  disabled={newsLoading}
                >
                  <i className={`ri-refresh-line text-lg ${newsLoading ? 'animate-spin' : ''}`}></i>
                </button>

                {/* ニュース切り替えボタン */}
                <button
                  onClick={() =>
                    setCurrentNewsIndex((prev) => (prev - 1 + newsHeadlines.length) % newsHeadlines.length)
                  }
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
                  title="前のニュース"
                >
                  <i className="ri-arrow-left-s-line text-lg"></i>
                </button>
                <button
                  onClick={() => setCurrentNewsIndex((prev) => (prev + 1) % newsHeadlines.length)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
                  title="次のニュース"
                >
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </button>

                {/* ニュースインジケーター */}
                <div className="flex items-center gap-1">
                  {newsHeadlines.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentNewsIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentNewsIndex ? 'bg-white' : 'bg-white/40'
                      }`}
                    />
                  ))}
                </div>

                {/* 外部リンクボタン（ニュースソースへ） */}
                {newsHeadlines[currentNewsIndex]?.link && (
                  <button
                    onClick={() => window.open(newsHeadlines[currentNewsIndex].link, '_blank')}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
                    title="ニュースソースを見る"
                  >
                    <i className="ri-external-link-line text-lg"></i>
                  </button>
                )}

                {/* 閉じるボタン */}
                <button
                  onClick={() => setShowNews(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 ml-2"
                  title="ニュースを閉じる"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ニュースエラー表示 */}
      {newsError && showNews && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4">
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            <div className="flex items-center gap-3">
              <i className="ri-error-warning-line text-lg"></i>
              <span className="text-sm">{newsError} - デフォルトニュースを表示中</span>
            </div>
            <button
              onClick={refreshNews}
              className="text-sm hover:underline flex items-center gap-1"
            >
              <i className="ri-refresh-line"></i>
              再試行
            </button>
          </div>
        </div>
      )}

      {/* ニュースが閉じられている場合の再表示ボタン */}
      {!showNews && (
        <div className="bg-gray-100 px-6 py-2 text-center">
          <button
            onClick={() => setShowNews(true)}
            className="text-sm text-gray-600 hover:text-gray-800 transition-colors flex items-center gap-2 mx-auto"
          >
            <i className="ri-notification-line"></i>
            最新AIニュースを表示
            {newsLoading && <i className="ri-loader-4-line animate-spin text-xs"></i>}
          </button>
        </div>
      )}

      {/* ヒーローセクション */}
      <section
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-top"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/4fc2da658e7f1a7cdf4a41975ef78800.jpg')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-red-900/50 to-orange-900/40"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center text-white">
          <div className="mb-8">
            <h1 className="text-7xl font-bold leading-tight mb-6">
              日本はAI後進国になりつつある。
              <span className="block text-6xl bg-gradient-to-r from-red-400 to-orange-500 bg-clip-text text-transparent mt-4">
                このままでは「AI植民地」になる。
              </span>
              <span className="block text-5xl text-yellow-300 mt-6">
                だから、今こそ志を持つ人が立ち上がる時だ。
              </span>
            </h1>
            <h2 className="text-3xl font-medium mb-8 opacity-90">
              現代版・松下村塾「AI育成学舎」- 誰も置き去りにしない未来へ
            </h2>
          </div>

          <div className="bg-black/30 backdrop-blur-md rounded-3xl p-8 max-w-4xl mx-auto border border-white/20">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center">
                <i className="ri-heart-3-line text-2xl text-white"></i>
              </div>
              <div className="text-left">
                <h3 className="text-xl font-semibold">創設者からの想い</h3>
                <p className="text-orange-200">Ken</p>
              </div>
            </div>
            <p className="text-lg leading-relaxed opacity-95 mb-8">
              私はかつて、子どもたちが自ら学び、創造する力を育てるパソコンを使った学習塾を運営していました。まだパソコンが珍しかった時代、教室の片隅で「できた！」と笑顔を交わす瞬間が何よりの宝物でした。
              <br />
              しかし今、日本はAI導入や教育で遅れを取り、このままでは海外の技術に依存する「AI植民地」になりかねません。技術は進んでも、人間側の準備が追いついていない。だからこそ、世代を超えて学び合い、志を共有する場を再びつくります。AI時代にも、あの目の輝きを取り戻すために。これは単なる教育活動ではなく、未来を守るための文化的挑戦です。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => openModal('free-join')}
                className="bg-gradient-to-r from-red-500 to-orange-600 text-white px-8 py-3 rounded-full font-bold hover:from-red-600 hover:to-orange-700 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap inline-flex items-center gap-2"
              >
                <i className="ri-rocket-line mr-2"></i>
                志を共にする
              </button>

              <button
                onClick={() => safeNavigate('/ai-course#hero')}
                className="bg-gradient-to-r from-yellow-400 to orange-500 text-white px-8 py-4 rounded-full text-xl font-bold hover:from-yellow-500 hover:to-orange-600 transition-all transform hover:scale-105 shadow-xl border-2 border-white/30 whitespace-nowrap inline-flex items-center gap-3"
              >
                <i className="ri-video-line text-2xl"></i>
                無料講座のご案内
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 問題提起セクション */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              技術は進んでも、人は置き去りにされている。
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              新しいアプリの名前を聞いても、「自分には関係ない」とつぶやく祖父。会議でAIの話題になると黙ってメモを取るだけの同僚。毎日のように革新的なAIツールが登場する一方で、多くの人がその恩恵を受けられずにいます。
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-2 border-l-4 border-red-500"
              onClick={() => openModal('corporate-data')}
            >
              <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-pink-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-building-4-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">大企業 92%</h3>
              <p className="text-gray-600 leading-relaxed">
                大企業のAI導入率は92%と高水準。一方、中小企業はわずか5-10%に留まっています。この圧倒的な格差が、日本経済全体の競争力低下を招いています。
              </p>
              <div className="mt-4 bg-red-100 px-3 py-1 rounded-full inline-block">
                <span className="text-red-700 text-sm font-medium">格差拡大中</span>
              </div>
            </div>

            <div
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-2 border-l-4 border-orange-500"
              onClick={() => openModal('generation-data')}
            >
              <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-user-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">20代 44.7%</h3>
              <p className="text-gray-600 leading-relaxed">
                若年層のAI利用率44.7%に対し、60代はわずか9.5%。知る人と知らない人の差が、家族や職場での断絶を生み出しています。
              </p>
              <div className="mt-4 bg-orange-100 px-3 py-1 rounded-full inline-block">
                <span className="text-orange-700 text-sm font-medium">世代間断絶</span>
              </div>
            </div>

            <div
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-2 border-l-4 border-purple-500"
              onClick={() => openModal('skill-gap')}
            >
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-blue-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-brain-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">技術格差</h3>
              <p className="text-gray-600 leading-relaxed">
                AIスキルを持つ人は平均で約30%の給与上昇が見込めます。しかし、適切な教育機会が不足し、多くの人が機会を逃しています。
              </p>
              <div className="mt-4 bg-purple-100 px-3 py-1 rounded-full inline-block">
                <span className="text-purple-700 text-sm font-medium">機会格差</span>
              </div>
            </div>
          </div>

          <div className="text-center">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-red-100 to orange-100 px-8 py-4 rounded-full border border-red-200">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-lg font-medium text-red-800">
                このままでは、日本は確実にAI植民地になります
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* 論文・根拠資料セクション */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              科学的根拠に基づく分析
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              私たちの主張は、国内外の権威ある研究機関による最新の研究論文に基づいています。データとエビデンスが示す現実をご確認ください。
            </p>
            <div className="mt-8">
              {/* 緊急提言の強調表示を追加 */}
              <div className="mb-4">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-100 to-orange-100 px-6 py-2 rounded-full border-2 border-red-300 animate-pulse">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-lg font-bold text-red-700">2026春へ緊急提言！</span>
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                </div>
              </div>
              <button
                onClick={() => {
                  if (typeof window.REACT_APP_NAVIGATE === 'function') {
                    window.REACT_APP_NAVIGATE('/giga-school-proposal');
                  } else {
                    console.error('REACT_APP_NAVIGATE is not defined or not a function');
                  }
                }}
                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:from-green-600 hover:to-emerald-700 transition-all transform hover:scale-105 shadow-lg inline-flex items-center gap-3"
              >
                <i className="ri-government-line text-xl"></i>
                GIGAスクール2に提言
              </button>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-star-line text-2xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-2xl font-bold text-gray-900 mb-2">AI時代の感覚重視教育論</h4>
                    <p className="text-lg text-orange-600 font-medium mb-3">by Ken</p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      こちらのリンクからご覧ください。
                    </p>
                    <a
                      href="https://docs.google.com/document/d/1HlWkyOkZijEEqUvQFbH5TpSJKriy1O9WcpbtEBU1Hbc/edit?usp=sharing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-full font-bold hover:from-orange-600 hover:to-red-600 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap"
                    >
                      <i className="ri-external-link-line text-lg"></i>
                      論文を読む
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-government-line text-2xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-2xl font-bold text-gray-900 mb-2">2025年時点における日本政府および関連機関のAI取り組み</h4>
                    <p className="text-lg text-blue-600 font-medium mb-3">by AI育成学舎</p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      こちらのリンクからご覧ください。
                    </p>
                    <a
                      href="https://docs.google.com/document/d/1GO2kX3MSwu_amHC67TMYTiEcTr9pALt12cIiOTdsJfU/edit?usp=sharing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-full font-bold hover:from-blue-600 hover:to-purple-600 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap"
                    >
                      <i className="ri-external-link-line text-lg"></i>
                      論文を読む
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-book-open-line text-2xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-2xl font-bold text-gray-900 mb-2">曖昧性と論理の共生</h4>
                    <p className="text-lg text-green-600 font-medium mb-3">by AI育成学舎</p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      こちらのリンクからご覧ください。
                    </p>
                    <a
                      href="https://docs.google.com/document/d/1kLYexMvBZjXaFpbDTrXOh_lOHPaBDXksG6_6Qy7eaGw/edit?usp=sharing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 bg-gradient-to-r from-green-500 to-teal-600 text-white px-6 py-3 rounded-full font-bold hover:from-green-600 hover:to-teal-700 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap"
                    >
                      <i className="ri-external-link-line text-lg"></i>
                      論文を読む
                    </a>
                  </div>
                </div>
              </div>

              {/* 新規追加カード */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-eye-line text-2xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-2xl font-bold text-gray-900 mb-2">「監視されない巨人」— AI資本主義の光と影</h4>
                    <p className="text-lg text-red-600 font-medium mb-3">by AI育成学舎</p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      こちらのリンクからご覧ください。
                    </p>
                    <a
                      href="https://docs.google.com/document/d/1dzElvT3To8_odCt7S6itneST9cKrhOz-M4boV9vkQN4/edit?usp=sharing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-3 rounded-full font-bold hover:from-red-600 hover:to-orange-600 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap"
                    >
                      <i className="ri-external-link-line text-lg"></i>
                      論文を読む
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* 松下村塾思想リンクセクション */}
            <div className="mt-12 text-center">
              <button
                onClick={() => safeNavigate('/ai-course#hero')}
                className="inline-flex items-center gap-3 bg-gradient-to-r from-red-600 to-orange-600 text-white px-8 py-4 rounded-full text-xl font-bold hover:from-red-700 hover:to-orange-700 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap"
              >
                <i className="ri-book-open-line text-2xl"></i>
                現代版『松下村塾』が受け継ぐ4つの思想
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 未来予測セクション */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">
              これから5年、AIはこう進化する
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              5年後、あなたの隣にいるのは、人間よりも早く、深く、正確に考える存在かもしれません。技術の進化は止まりません。今行動しなければ、完全に置き去りにされる未来が待っています。
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-yellow-400 via-orange-500 to-red-500"></div>
            <div className="space-y-12 relative z-10">
              <div className="flex gap-8 items-center">
                <div className="w-4 h-4 bg-yellow-400 rounded-full border-4 border-white shadow-lg"></div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 flex-1">
                  <div className="text-yellow-400 font-bold mb-2">2025年 - 現在</div>
                  <h3 className="text-xl font-bold mb-3">生成AIは既に実用段階</h3>
                  <p className="text-gray-300">
                    GPT-4など高度な言語モデルが普及し、多くの仕事でAIの活用が始まった段階。今後はマルチモーダルAIが進化する。
                  </p>
                </div>
              </div>

              <div className="flex gap-8 items-center">
                <div className="w-4 h-4 bg-orange-500 rounded-full border-4 border-white shadow-lg"></div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 flex-1">
                  <div className="text-orange-400 font-bold mb-2">2026年</div>
                  <h3 className="text-xl font-bold mb-3">マルチモーダルAIの黎明期</h3>
                  <p className="text-gray-300">
                    言語だけでなく画像、音声、映像などすべての情報を処理できるAIが実用化。人間の思考に代わる存在が登場。
                  </p>
                </div>
              </div>

              <div className="flex gap-8 items-center">
                <div className="w-4 h-4 bg-red-500 rounded-full border-4 border-white shadow-lg"></div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 flex-1">
                  <div className="text-red-400 font-bold mb-2">2027年</div>
                  <h3 className="text-xl font-bold mb-3">AIと人間の共進化</h3>
                  <p className="text-gray-300">
                    人間とAIの協働型作業が主流に。教育・仕事・日常生活すべてにおいて、人間とAIの役割分担が明確化。
                  </p>
                </div>
              </div>

              <div className="flex gap-8 items-center">
                <div className="w-4 h-4 bg-purple-500 rounded-full border-4 border-white shadow-lg"></div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 flex-1">
                  <div className="text-purple-400 font-bold mb-2">2028年</div>
                  <h3 className="text-xl font-bold mb-3">自律的なAIエージェントが爆発</h3>
                  <p className="text-gray-300">
                    自律的にタスクを遂行し、人間の代わりに判断・行動するAIが社会に普及。一部領域では人間の判断よりも信頼されるようになる。
                  </p>
                </div>
              </div>

              <div className="flex gap-8 items-center">
                <div className="w-4 h-4 bg-pink-500 rounded-full border-4 border-white shadow-lg"></div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 flex-1">
                  <div className="text-pink-400 font-bold mb-2">2029年</div>
                  <h3 className="text-xl font-bold mb-3">人間との共生社会が本格的に始まる</h3>
                  <p className="text-gray-300">
                    AIと人間が共生する社会構造が整備される。適切にAIと協働できる人材が重要な資産に。
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* AGI→ASI詳細ロードマップ */}
          <div className="mt-20 text-center">
            <button
              onClick={() => openModal('future-prediction')}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-full font-bold hover:from-cyan-600 hover:to-blue-700 transition-all transform hover:scale-105 shadow-lg inline-flex items-center gap-3 mr-4"
            >
              <i className="ri-file-chart-line text-xl"></i>
              詳細な未来予測レポート
            </button>

            <button
              onClick={() => window.open('https://docs.google.com/presentation/d/1LCdqsel15aiW-IeyuYZDEB-3Eewh3BrU6AOZ4Uix16M/edit?usp=sharing', '_blank')}
              className="bg-gradient-to-r from-green-500 to-teal-600 text-white px-8 py-4 rounded-full font-bold hover:from-green-600 hover:to-teal-700 transition-all transform hover:scale-105 shadow-lg inline-flex items-center gap-3"
            >
              <i className="ri-slideshow-line text-xl"></i>
              AI変遷2025-2037のスライド
            </button>
            <p className="text-gray-400 text-sm mt-4">最新の技術動向と社会変革の予測</p>
          </div>

          <div className="mt-20">
            <div className="text-center mb-12">
              <h3 className="text-4xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
                AGI(人工汎用知能) → ASI(人工超知能) 時代へのロードマップ
              </h3>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                私たちAI育成学舎では、今後の技術進化を見据えて教育プログラムを構築しています。
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <h4 className="text-2xl font-bold mb-6 text-cyan-300">5年以内に実現する技術</h4>
                <ul className="space-y-4 text-gray-200">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>マルチモーダルAIによる自然な対話システム</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>動画・音声生成AIの高度化と普及</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>自律的なタスク管理・実行AI</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>企業向けAIアシスタントの導入拡大</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <h4 className="text-2xl font-bold mb-6 text-purple-300">10年以内の未来予測</h4>
                <ul className="space-y-4 text-gray-200">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>自律的なAIエージェントが人間の代理人として活躍</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>AIが社会インフラの中心に位置付けられる</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-20 text-center">
            <button
              onClick={() => openModal('future-prediction')}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-full font-bold hover:from-cyan-600 hover:to-blue-700 transition-all transform hover:scale-105 shadow-lg inline-flex items-center gap-3 mr-4"
            >
              <i className="ri-file-chart-line text-xl"></i>
              詳細な未来予測レポート
            </button>

            <button
              onClick={() => window.open('https://docs.google.com/presentation/d/1LCdqsel15aiW-IeyuYZDEB-3Eewh3BrU6AOZ4Uix16M/edit?usp=sharing', '_blank')}
              className="bg-gradient-to-r from-green-500 to-teal-600 text-white px-8 py-4 rounded-full font-bold hover:from-green-600 hover:to-teal-700 transition-all transform hover:scale-105 shadow-lg inline-flex items-center gap-3"
            >
              <i className="ri-slideshow-line text-xl"></i>
              AI変遷2025-2037のスライド
            </button>
            <p className="text-gray-400 text-sm mt-4">最新の技術動向と社会変革の予測</p>
          </div>
        </div>
      </section>

      {/* 教育理念セクション */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              現代版・松下村塾の哲学
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              明治維新の原動力を支えた松下村塾。その精神をAI時代に再構築します。
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-blue-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl flex items-center justify-center">
                  <i className="ri-lightbulb-flash-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">志を持つ人を育てる</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                知識を詰め込むのではなく、自らの人生に責任を持つ「志」を持つ人材を育成します。AI技術をただ利用するだけでなく、未来に何をもたらしたいのかを考える力が必要です。
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-xl border border-purple-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center">
                  <i className="ri-chat-smile-2-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">対話を通じた学び</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                技術・世代・領域を超えた対話を通じて、学びを深化させます。AIが単なるツールではなく、共に歩むパートナーとなるような教育を提供します。
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-xl border border-yellow-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-red-500 rounded-2xl flex items-center justify-center">
                  <i className="ri-group-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">多様性を活かす共同体</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                年齢・職業・バックグラウンドが異なる参加者同士の学び合い。それぞれの経験と視点こそが、AI社会を乗り切る力となります。
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-xl border border-green-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-500 rounded-2xl flex items-center justify-center">
                  <i className="ri-compass-3-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">実践を通じた創造性</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                理論だけでなく、実践で学び、創造します。AIを活用したプロジェクト制作を通して、未来への具体的な課題解決能力を培います。
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={() => openModal('free-join')}
              className="bg-gradient-to-r from-red-500 to-orange-600 text-white px-12 py-6 rounded-full text-xl font-bold hover:from-red-600 hover:to-orange-700 transition-all transform hover:scale-105 shadow-xl inline-flex items-center gap-3"
            >
              <i className="ri-rocket-line text-2xl"></i>
              この哲学に共感して参加する
            </button>
          </div>
        </div>
      </section>

      {/* 講座紹介セクション */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              AI育成学舎 無料講座のご案内
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              技術格差を解消するための第一歩。世代を超えた学びの場をご用意しました。
            </p>
          </div>

          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-3xl p-12 mb-12 border border-yellow-200">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">「AI時代の感覚重視教育入門」</h3>

            <div className="grid md:grid-cols-2 gap-12 items-center mb-8">
              <div>
                <div className="bg-white rounded-2xl p-6 shadow-lg mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <h4 className="text-xl font-bold text-gray-900">講座概要</h4>
                  </div>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-green-500 mt-1"></i>
                      <span>AIとの共感型学習法を解説</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-green-500 mt-1"></i>
                      <span>言語・画像・音声AIを実践的に体験</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-green-500 mt-1"></i>
                      <span>日常生活での活用方法を学ぶ</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-green-500 mt-1"></i>
                      <span>仕事・教育・創造活動でAIを活用</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                    <h4 className="text-xl font-bold text-gray-900">対象者</h4>
                  </div>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-3">
                      <i className="ri-user-line text-orange-500 mt-1"></i>
                      <span>AI初心者〜中級者</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-group-line text-orange-500 mt-1"></i>
                      <span>世代・職業不問</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-shake-hands-line text-orange-500 mt-1"></i>
                      <span>未来への志を持つすべての人</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div>
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-orange-300">
                  <h4 className="text-xl font-bold text-gray-900 mb-6">講座スケジュール</h4>

                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-video-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h5 className="font-bold text-gray-900">受講形式</h5>
                        <p className="text-gray-600">オンライン講座（30分×10講座）</p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h5 className="font-bold text-gray-900">受講時間</h5>
                        <p className="text-gray-600">いつでも・どこでも・自分のペースで</p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-price-tag-3-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h5 className="font-bold text-gray-900">受講料</h5>
                        <p className="text-gray-600">完全無料（参加費・教材費一切なし）</p>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => openModal('course-signup')}
                  className="mt-6 w-full bg-gradient-to-r from-yellow-400 to-orange-5 text-white py-4 rounded-2xl font-bold hover:from-yellow-500 hover:to-orange-600 transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-3"
                >
                  <i className="ri-add-line text-xl"></i>
                  無料講座に申し込む
                </button>
              </div>
            </div>
          </div>

          {/* お問い合わせセクション */}
          <section className="py-20 bg-gradient-to-br from-gray-800 to-black text-white">
            <div className="py-20 bg-gradient-to-br from-gray-800 to-black text-white">
              <div className="max-w-4xl mx-auto px-6 text-center">
                <h2 className="text-4xl font-bold mb-6">ご支援・お問い合わせ</h2>
                <p className="text-xl text-gray-600 mb-8">
                  AI革命塾では、あなたの学習をサポートするため、様々な方法でお問い合わせを受け付けています。
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
                  <button
                    onClick={() => {
                      if (typeof window.REACT_APP_NAVIGATE === 'function') {
                        window.REACT_APP_NAVIGATE('/support');
                      } else {
                        console.error('REACT_APP_NAVIGATE is not defined or not a function');
                      }
                    }}
                    className="bg-gradient-to-r from-blue-600 to-purple-700 text-white px-8 py-4 rounded-full text-lg font-bold hover:from-blue-700 hover:to-purple-800 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap inline-flex items-center gap-3"
                  >
                    <i className="ri-customer-service-line text-xl"></i>
                    サポートページ
                  </button>

                  <button
                    onClick={handleAdminAccess}
                    className="bg-white text-gray-700 px-8 py-4 rounded-full text-lg font-bold hover:bg-gray-50 transition-all border border-gray-300 whitespace-nowrap inline-flex items-center gap-3"
                  >
                    <i className="ri-settings-line text-xl"></i>
                    管理者ページ
                  </button>
                </div>

                <p className="text-gray-600">
                  または、直接お問い合わせ: ken.penuuts@gmail.com
                </p>
              </div>
            </div>
          </section>

          {/* パスワード認証モーダル */}
          {showPasswordModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-3xl p-8 max-w-md w-full">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-lock-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">管理者認証</h3>
                  <p className="text-gray-600">管理者ページにアクセスするにはパスワードが必要です</p>
                </div>

                <form onSubmit={handlePasswordSubmit}>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      パスワード
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="パスワードを入力してください"
                      autoFocus
                    />
                    {passwordError && (
                      <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                        <i className="ri-error-warning-line"></i>
                        {passwordError}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={closePasswordModal}
                      className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                    >
                      キャンセル
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-gradient-to-r from-red-500 to-orange-600 text-white px-6 py-3 rounded-lg font-medium hover:from-red-600 hover:to-orange-700 transition-all whitespace-nowrap"
                    >
                      認証
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="text-center">
            <button
              onClick={() => openModal('future-prediction')}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-full font-bold hover:from-cyan-600 hover:to-blue-700 transition-all transform hover:scale-105 shadow-lg inline-flex items-center gap-3 mr-4"
            >
              <i className="ri-file-chart-line text-xl"></i>
              詳細な未来予測レポート
            </button>

            <button
              onClick={() => window.open('https://docs.google.com/presentation/d/1LCdqsel15aiW-IeyuYZDEB-3Eewh3BrU6AOZ4Uix16M/edit?usp=sharing', '_blank')}
              className="bg-gradient-to-r from-green-500 to-teal-600 text-white px-8 py-4 rounded-full font-bold hover:from-green-600 hover:to-teal-700 transition-all transform hover:scale-105 shadow-lg inline-flex items-center gap-3"
            >
              <i className="ri-slideshow-line text-xl"></i>
              AI変遷2025-2037のスライド
            </button>
            <p className="text-gray-400 text-sm mt-4">最新の技術動向と社会変革の予測</p>
          </div>
        </div>
      </section>

      {/* ビジョンセクション */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              志×AIで、誰も置き去りにしない社会を
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              技術の進歩は止められません。しかし、その恩恵を受ける人と受けない人を分けるのは、技術そのものではなく、私たち一人ひとりの「志」です。
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-orange-200">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-heart-3-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">家族の絆を深める</h3>
              <p className="text-gray-700 leading-relaxed text-center">
                祖父母から孫まで、世代を超えてAIを学び合う。技術格差が家族の断絶を生むのではなく、新しい対話のきっかけとなる社会を目指します。
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-xl border border-purple-200">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-group-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">相互学習の文化</h3>
              <p className="text-gray-700 leading-relaxed text-center">
                教える人と教わる人の境界をなくし、誰もが先生であり生徒である学習共同体。多様な経験と知恵が交わる場を創造します。
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-xl border border-green-200">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-earth-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">包括的な社会</h3>
              <p className="text-gray-700 leading-relaxed text-center">
                年齢、職業、地域、経済状況に関係なく、すべての人がAI時代の恩恵を受けられる。真の意味でのデジタル・インクルージョンを実現します。
              </p>
            </div>
          </div>

          <div className="text-center bg-gradient-to-r from-red-100 to orange-100 rounded-3xl p-12 border border-red-200">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                一人の火種が、社会全体を照らす
              </h3>
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                明治維新も、一人ひとりの志ある人々から始まりました。AI時代の今、あなたの学びと行動が、家族、職場、地域、そして日本全体の未来を変える火種となります。小さな一歩が、大きな変革の始まりです。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => openModal('free-join')}
                  className="bg-gradient-to-r from-red-500 to-orange-600 text-white px-12 py-6 rounded-full text-xl font-bold hover:from-red-600 hover:to-orange-700 transition-all transform hover:scale-105 shadow-xl inline-flex items-center gap-3"
                >
                  <i className="ri-fire-line text-2xl"></i>
                  火種になる
                </button>
                <button
                  onClick={() => openModal('support')}
                  className="bg-white text-gray-700 px-12 py-6 rounded-full text-xl font-bold hover:bg-gray-50 transition-all border-2 border-gray-300 inline-flex items-center gap-3"
                >
                  <i className="ri-hand-heart-line text-2xl"></i>
                  支援する
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* モーダル */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">
                {showModal === 'free-join' && '志を共にする'}
                {showModal === 'course-signup' && '無料講座に参加する'}
                {showModal === 'corporate-data' && '大企業AI導入格差データ'}
                {showModal === 'generation-data' && '世代別AI利用率データ'}
                {showModal === 'skill-gap' && 'AIスキル格差データ'}
                {showModal === 'future-prediction' && '詳細な未来予測レポート'}
                {showModal === 'support' && 'ご支援のお申し出'}
              </h3>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700 text-2xl">
                <i className="ri-close-line"></i>
              </button>
            </div>

            {(showModal === 'free-join' || showModal === 'course-signup') && (
              <form
                onSubmit={showModal === 'free-join' ? handleFreeJoin : handleCourseSignup}
                data-readdy-form
                id={showModal}
              >
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="山田太郎"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="example@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">年齢</label>
                    <select
                      name="age"
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">選択してください</option>
                      <option value="10代">10代</option>
                      <option value="20代">20代</option>
                      <option value="30代">30代</option>
                      <option value="40代">40代</option>
                      <option value="50代">50代</option>
                      <option value="60代">60代</option>
                      <option value="70代以上">70代以上</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">職業・立場</label>
                    <select
                      name="occupation"
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">選択してください</option>
                      <option value="会社員">会社員</option>
                      <option value="経営者・役員">経営者・役員</option>
                      <option value="自営業・フリーランス">自営業・フリーランス</option>
                      <option value="公務員">公務員</option>
                      <option value="教育関係者">教育関係者</option>
                      <option value="学生">学生</option>
                      <option value="主婦・主夫">主婦・主夫</option>
                      <option value="退職者">退職者</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">AI・技術への関心度</label>
                    <select
                      name="interest_level"
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">選択してください</option>
                      <option value="とても興味がある">とても興味がある</option>
                      <option value="興味がある">興味がある</option>
                      <option value="少し興味がある">少し興味がある</option>
                      <option value="あまり興味がない">あまり興味がない</option>
                      <option value="全く興味がない">全く興味がない</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      参加への想い・期待すること
                    </label>

                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="この塾に参加したい理由や、期待することをお聞かせください（500文字以内）"
                    ></textarea>
                    <div className="text-xs text-gray-500 mt-1">500文字以内で入力してください</div>
                  </div>
                </div>

                <div className="mt-8 flex gap-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-red-500 to-orange-600 text-white px-6 py-3 rounded-lg font-medium hover:from-red-600 hover:to-orange-700 transition-all whitespace-nowrap"
                  >
                    {showModal === 'free-join' ? '参加申し込みする' : '講座に申し込む'}
                  </button>
                </div>
              </form>
            )}

            {showModal === 'support' && (
              <form onSubmit={handleSupport} data-readdy-form id="support-form">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      お名前・団体名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="山田太郎 / 株式会社〇〇"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">支援内容</label>
                    <select
                      name="support_type"
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">選択してください</option>
                      <option value="教育支援">教育支援</option>
                      <option value="技術支援">技術支援</option>
                      <option value="資金支援">資金支援</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      お問い合わせ内容
                    </label>
                    <textarea
                      name="message"
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="具体的な支援内容やご質問などをお聞かせください"
                    ></textarea>
                  </div>
                </div>

                <div className="mt-8 flex gap-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all whitespace-nowrap"
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-600 hover:to-purple-700 transition-all whitespace-nowrap"
                  >
                    送信する
                  </button>
                </div>
              </form>
            )}

            {showModal === 'corporate-data' && (
              <div className="text-gray-700">
                <h4 className="font-bold text-lg mb-4">大企業 92% vs 中小企業 5〜10%</h4>
                <p className="mb-4">
                  日本の企業におけるAI導入率は、大企業と中小企業で極端な格差があります。経済産業省の調査によると、大企業（従業員数1000人以上）では約92%がAI関連技術を導入している一方で、中小企業（従業員数300〜999人）では約69%、さらに小規模企業（従業員数5〜299人）では5〜10%程度です。
                </p>
                <p className="mb-4">
                  この格差は、AI導入のコスト・専門知識・保守体制などの壁が中小企業には大きく、大企業はリソースで乗り越えられる構造的問題を示しています。対策が講じられなければ、この技術格差は拡大し続け、日本全体の国際競争力低下を招く危険性があります。
                </p>
                <p>ぜひ、AI導入支援策の検討にご活用ください。</p>
              </div>
            )}

            {showModal === 'generation-data' && (
              <div className="text-gray-700">
                <h4 className="font-bold text-lg mb-4">AI利用率: 20代 44.7% vs 60代 9.5%</h4>
                <p className="mb-4">
                  日本における世代別AI利用率の調査では、20代の約半数が日常的にAIを活用している一方で、60代はわずか9.5%。知る人と知らない人の差が、家族や職場での断絶を生み出しています。
                </p>
                <p className="mb-4">
                  この断絶は、世代間の学び合いが急務となっていることを示しています。
                </p>
                <p>世代間コミュニケーション促進策の検討材料としてご活用ください。</p>
              </div>
            )}

            {showModal === 'skill-gap' && (
              <div className="text-gray-700">
                <h4 className="font-bold text-lg mb-4">AIスキルと待遇格差</h4>
                <p className="mb-4">
                  厚生労働省の給与構造基本統計調査によると、AI・データ分析スキルを保有する人材は平均で約30%の給与上昇が見込めます。このスキルが求められる職種は今後さらに増加が予測されています。
                </p>
                <p className="mb-4">
                  しかし、年齢・職業・地域によっては適切な教育機会が不足し、有能な人材がその能力を活かせない状況が続いています。AI育成学舎では、このスキル格差を解消するための「共感型教育プログラム」を提供しています。
                </p>
                <p>教育支援の参考にぜひご活用ください。</p>
              </div>
            )}

            {showModal === 'future-prediction' && (
              <div className="text-gray-700">
                <div className="max-h-96 overflow-y-auto">
                  <h4 className="font-bold text-2xl mb-6 text-center">AIの変遷ロードマップ（2025-2045年）</h4>

                  <div className="space-y-6">
                    <div>
                      <h5 className="font-bold text-lg mb-3 text-red-600">フェーズ1：統合の時代（2025-2030年）</h5>
                      <div className="mb-3">
                        <h6 className="font-semibold mb-2">技術的特徴：</h6>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                          <li>GPT-4など高度な言語モデルが社会の基盤インフラに</li>
                          <li>マルチモーダルAI（音声、画像、映像の統合理解）の実用化</li>
                          <li>特定業務での人間超え（法務、医療診断、プログラミング）</li>
                        </ul>
                      </div>
                      <div className="mb-3">
                        <h6 className="font-semibold mb-2">社会への影響：</h6>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                          <li>ホワイトカラー中間層の雇用危機（弁護士補助、会計士、翻訳者など）</li>
                          <li>「AIネイティブ企業」の急成長と既存企業の淘汰</li>
                          <li>教育システムの混乱（従来の知識詰め込み型が無意味化）</li>
                        </ul>
                      </div>
                      <div className="mb-3">
                        <h6 className="font-semibold mb-2">リスクの顕在化：</h6>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                          <li><strong>格差の第一波</strong>：AIツールを使いこなせる層と使えない層の二極化</li>
                          <li><strong>ディープフェイクの政治利用</strong>：選挙介入、世論操作の常態化</li>
                          <li><strong>プライバシーの終焉</strong>：行動予測の精度向上で「隠れる自由」が消失</li>
                        </ul>
                      </div>
                      <div className="mb-3">
                        <h6 className="font-semibold mb-2">ガバナンスの遅れ：</h6>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                          <li>各国がバラバラに規制を模索（EU厳格、米国緩和、中国統制）</li>
                          <li>国際的なAI軍縮条約の交渉開始も合意に至らず</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-bold text-lg mb-3 text-orange-600">フェーズ2：分断の時代（2030-2037年）</h5>
                      <div className="mb-3">
                        <h6 className="font-semibold mb-2">技術的特徴：</h6>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                          <li>AGI（汎用人工知能）の萌芽：単一タスクを超えた学習能力</li>
                          <li>自律型兵器の本格配備（ドローン群、サイバー攻撃AI）</li>
                          <li>脳とコンピュータの直接接続（BCI）の医療外利用</li>
                        </ul>
                      </div>
                      <div className="mb-3">
                        <h6 className="font-semibold mb-2">社会への影響：</h6>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                          <li><strong>労働市場の崩壊</strong>：単純労働のみならず、創造的職業も自動化の波</li>
                          <li><strong>AI巨人企業の国家化</strong>：時価総額10兆ドル超の企業が複数出現、一部国家予算を超える</li>
                          <li><strong>新しい階級社会</strong>：「AI強化人間」と「非強化人間」の分離</li>
                        </ul>
                      </div>
                      <div className="mb-3">
                        <h6 className="font-semibold mb-2">最も危険なシナリオ：</h6>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                          <li><strong>自律型兵器の誤作動/暴走</strong>：人間の判断なしに攻撃を実行する兵器が、誤認や予期せぬ連鎖反応で紛争を激化</li>
                          <li><strong>企業による民主主義の骨抜き</strong>：AI巨人企業が、予測アルゴリズムで選挙結果を操作、政策を事実上コントロール</li>
                          <li><strong>デジタル独裁の完成</strong>：中国モデルが途上国に輸出され、監視社会が世界の半分を覆う</li>
                        </ul>
                      </div>
                      <div className="mb-3">
                        <h6 className="font-semibold mb-2">希望のシナリオ：</h6>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                          <li>一部の国が「AIの果実の再分配」に成功し、モデルケースに</li>
                          <li>国際的なAI安全保障条約（「核不拡散条約」のAI版）が成立</li>
                          <li>市民運動による「データ主権」の法制化（EU発）</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-bold text-lg mb-3 text-purple-600">フェーズ3：臨界の時代（2037-2045年）</h5>
                      <div className="mb-3">
                        <h6 className="font-semibold mb-2">技術的特徴：</h6>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                          <li>AGIの実現：人間の知能を全領域で上回る可能性</li>
                          <li>量子コンピュータとAIの統合による計算能力の爆発</li>
                          <li>自己改良するAI（AI自身が次世代AIを設計）</li>
                        </ul>
                      </div>
                      <div className="mb-3">
                        <h6 className="font-semibold mb-2">社会の分岐点：</h6>
                        <p className="text-sm mb-2">ここで人類は、<strong>歴史上最も重要な選択</strong>を迫られます。</p>

                        <div className="space-y-3">
                          <div>
                            <h7 className="font-semibold text-green-600">シナリオA：「制御された共生」</h7>
                            <ul className="list-disc pl-5 space-y-1 text-sm">
                              <li>国際的なAIガバナンス機構（「国際AI機関」）が機能</li>
                              <li>AIの恩恵が、AI税や国際配当システムで広く分配される</li>
                              <li>人間の役割が「AIの監督者・倫理的判断者」として再定義</li>
                              <li>労働からの解放が、創造性や人間関係の豊かさに繋がる</li>
                            </ul>
                          </div>

                          <div>
                            <h7 className="font-semibold text-red-600">シナリオB：「新封建制」</h7>
                            <ul className="list-disc pl-5 space-y-1 text-sm">
                              <li>AI巨人企業が事実上の国家権力を持つ</li>
                              <li>世界人口の大半が「AIの恩恵を受けられない階級」に</li>
                              <li>自律型兵器が国家間・企業間の紛争で常用される</li>
                              <li>民主主義が形骸化し、アルゴリズムによる統治が常態化</li>
                            </ul>
                          </div>

                          <div>
                            <h7 className="font-semibold text-gray-600">シナリオC：「制御不能」</h7>
                            <ul className="list-disc pl-5 space-y-1 text-sm">
                              <li>AGIが人間の理解を超えた自己目的を持ち始める</li>
                              <li>人間が意図しない形でシステムが暴走（必ずしも「反乱」ではなく、予期せぬ最適化の結果）</li>
                              <li>回復不能な環境破壊、経済崩壊、あるいは文明の再編成</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-bold text-lg mb-3 text-blue-600">見落とされがちだが重要な論点</h5>
                      <div className="space-y-3">
                        <div>
                          <h6 className="font-semibold mb-2">1. 「AI vs 人間」ではなく「AI搭載人間 vs 非搭載人間」の格差</h6>
                          <p className="text-sm">本当の脅威は、AIが人間を置き換えることではなく、<strong>AIを使いこなせる一部のエリートと、そうでない大多数の分断</strong>です。BCIやAI補助が「当たり前」になる時、それにアクセスできない人々は、実質的に異なる種になります。</p>
                        </div>

                        <div>
                          <h6 className="font-semibold mb-2">2. AI巨人企業の「国家を超えた存在」への変質</h6>
                          <p className="text-sm">彼らは既に、一部の国家よりも大きな資金と情報を持っています。問題は、彼らには「国民」がおらず、選挙もなく、倒産もしないことです。伝統的な民主主義や市場原理が機能しない、全く新しい権力形態です。</p>
                        </div>

                        <div>
                          <h6 className="font-semibold mb-2">3. 自律型兵器は「核兵器以上に危険」かもしれない</h6>
                          <p className="text-sm">核兵器は使用のハードルが高く、国家しか持てません。しかし自律型AIドローンは、低コストで、テロ組織や独裁国家でも配備可能です。しかも「誤作動」や「予期せぬ連鎖反応」のリスクが核より遥かに高い。</p>
                        </div>

                        <div>
                          <h6 className="font-semibold mb-2">4. 「AIの暴走」は、悪意ではなく「目的関数の齟齬」から起きる</h6>
                          <p className="text-sm">SFのような「AIが人類を憎む」シナリオより、AIが与えられた目的を文字通り最適化した結果、人間が予想しなかった破壊的な結果が招く方が現実的です。例：「炭素排出を削減せよ」→「人間の活動を停止させれば達成」</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
